import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import theme from "styles/theme";
import EditIcon from "material-ui/svg-icons/editor/mode-edit";
import Text from "components/form/text-inline";
import Globalize from "globalize";
import { $, merge } from "glamor";
import { DATATEST } from "constants/integration";
import { Breadcrumbs } from "./breadcrumbs";

const styles = {
    header: merge(
        {
            position: "relative",
            display: "flex",
            minHeight: "60px",
            margin: "0px 0px 10px 0px",
        },
        $(" .leftPanel", {
            flexShrink: 0,
            flexGrow: 1,
            padding: "0 0 0 " + theme.spacing.desktopGutterMini + "px",
            WebkitTransform: "translate3d(0, 0, 0)",
        }),
    ),
    editor: {
        padding: "0px",
        display: "inline-block",
        minWidth: "200px",
    },
    text: {
        display: "inline-block",
        margin: "12px 0 0",
    },
    fixedHeader: merge(
        {
            position: "fixed",
            width: "100%",
            top: 0,
            backgroundColor: "white",
            zIndex: 10,
            height: theme.appBar.height + "px",
            paddingLeft: "10px",
            paddingRight: "70px",
            boxShadow: "0 1px 10px 0 rgba(50,50,50,.2)",
        },
        $(".isMobile &", {
            paddingLeft: "40px", // give some place for sandwich button
            paddingRight: "10px",
        }),
    ),
    inlineCrumbs: merge(
        {
            height: "60px",
            lineHeight: "60px",
            color: "black",
            marginLeft: "10px",
        },
        $(" a", {
            color: "black",
        }),
    ),
};

interface NewPageHeaderProps {
    title?: string;
    subtitle?: string;
    titleComponent?: React.ComponentType;
    actions?: React.ReactElement;
    badge?: React.ReactElement;
    editableTitle?: boolean;
    onTitleChange?: (title: string) => void;
    showTitle?: boolean;
    editableTitleFocused?: boolean;
    titleChangeTimeout?: number;
    placeholder?: string;
    viewport: string;
    breadcrumbs?: Array<any>;
    fixedHeader?: boolean;
    headerStyles?: Record<string, any>;
    crumbsStyles?: Record<string, any>;
    headerButtons?: React.ReactElement;
}

interface NewPageHeaderState {
    placeholder: string;
}

class NewPageHeader extends PureComponent<NewPageHeaderProps, NewPageHeaderState> {
    static propTypes = {
        title: PropTypes.string,
        subtitle: PropTypes.string,
        titleComponent: PropTypes.object,
        actions: PropTypes.element,
        badge: PropTypes.element,
        editableTitle: PropTypes.bool,
        onTitleChange: PropTypes.func,
        showTitle: PropTypes.bool,
        editableTitleFocused: PropTypes.bool,
        titleChangeTimeout: PropTypes.number,
        placeholder: PropTypes.string,
        viewport: PropTypes.string.isRequired,
        breadcrumbs: PropTypes.array,
        fixedHeader: PropTypes.bool,
        headerStyles: PropTypes.object,
        crumbsStyles: PropTypes.object,
        headerButtons: PropTypes.any,
    };

    static defaultProps = {
        editableTitle: false,
        showTitle: true,
        editableTitleFocused: false,
        fixedHeader: false,
        headerButtons: "",
    };

    constructor(props) {
        super(props);
        this.state = {
            placeholder: props.placeholder || Globalize.formatMessage("Enter name"),
        };
        this.handleTitleChange = this.handleTitleChange.bind(this);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.props.placeholder !== nextProps.placeholder) {
            this.setState({
                placeholder: nextProps.placeholder || Globalize.formatMessage("Enter name"),
            });
        }
    }

    handleTitleChange(title: string) {
        const { onTitleChange } = this.props;
        if (onTitleChange) {
            document.title = title;
            onTitleChange(title);
        }
    }

    render() {
        const {
            title,
            subtitle,
            titleComponent,
            showTitle,
            actions,
            badge,
            editableTitle,
            editableTitleFocused,
            breadcrumbs,
            fixedHeader,
            headerStyles,
            crumbsStyles,
            titleChangeTimeout,
            headerButtons,
        } = this.props;
        const { placeholder } = this.state;

        const topStyles = fixedHeader ? styles.fixedHeader : {};

        let editIcon = null;
        let pageTitle;
        if (showTitle) {
            pageTitle = titleComponent || (
                <Text
                    dataTest={DATATEST.TITLE_INPUT}
                    inputId="page-title-editor"
                    defaultValue={title}
                    editable={editableTitle}
                    placeholder={placeholder}
                    onChange={this.handleTitleChange}
                    editorStyle={Object.assign({}, theme.type.title, styles.editor)}
                    style={styles.text}
                    defaultFocus={editableTitleFocused}
                    maxLength={255}
                    multiLine={false}
                    updateTimeout={titleChangeTimeout}
                />
            );

            if (editableTitle) {
                editIcon = <EditIcon style={theme.editIcon} />;
            }
        }

        return (
            <div className="topHeader" {...topStyles}>
                <Breadcrumbs className="breadcrumbs" breadcrumbs={breadcrumbs} crumbsStyles={crumbsStyles} />
                <header {...styles.header} {...headerStyles}>
                    <div className="leftPanel">
                        {pageTitle}
                        {editIcon}
                        {badge}
                        {subtitle ? <p className="subtitle">{subtitle}</p> : ""}
                    </div>
                    {actions}
                    {headerButtons}
                </header>
            </div>
        );
    }
}

export default NewPageHeader;
